import { UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import {
  Empty,
  Form,
  FormItemProps,
  Select,
  SelectProps,
  Spin,
  Tooltip,
} from 'antd';
import { SelectValue } from 'antd/es/select';
import { NamePath } from 'antd/lib/form/interface';
import useRTKQueryInfiniteLoad from 'hooks/useRTKQueryInfiniteLoad';
import i18next from 'i18next';
import { isString, uniqBy } from 'lodash-es';
import React, { useState } from 'react';
import { convertDataToSelectOptions } from 'utils/tools';

export interface Props<TRecord> extends SelectProps<SelectValue> {
  useQuery: UseQuery<any>;
  queryParams?: object;
  skip?: boolean;
  skipUntilFocus?: boolean;
  initValues?: TRecord[];
  labelProp?: string;
  valueProp?: string;
  uniqueProp?: keyof TRecord;
  searchKey?: string;
  isShowTooltip?: boolean;
  name: NamePath;
  formItemProps?: FormItemProps;
  className?: string;
  convertDataToOptions?: (data: TRecord[]) => {
    value: string | number | null;
    label: string | React.ReactNode;
  }[];
  emptyComponent?: React.ReactNode;
  placeholder?: string;
}

function InfiniteSelect<TRecord>({
  useQuery,
  convertDataToOptions,
  labelProp = 'name',
  valueProp = 'id',
  uniqueProp = null,
  searchKey = 'searchKey',
  initValues = [],
  queryParams,
  isShowTooltip = true,
  className,
  formItemProps,
  name,
  skip,
  skipUntilFocus = true,
  emptyComponent = <Empty />,
  placeholder,
  ...rest
}: Props<TRecord>) {
  const [isFocused, setIsFocused] = useState(false);

  const skipApiCondition = skipUntilFocus ? !isFocused : skip;

  const { data, isFetching, loadMore, onSearch, refresh } =
    useRTKQueryInfiniteLoad<TRecord>({
      useQuery,
      searchKey,
      queryParams,
      skip: skipApiCondition,
    });

  const items = uniqBy([...initValues, ...data], uniqueProp);

  const formattedData = convertDataToOptions
    ? convertDataToOptions(items)
    : convertDataToSelectOptions(items, labelProp, valueProp);

  const Spinning = <Spin className="mx-12 my-4" />;

  const options = isFetching
    ? [
        ...formattedData,
        {
          value: null,
          label: Spinning,
        },
      ]
    : formattedData;

  const onFocus = () => {
    if (!isFocused) {
      setIsFocused(true);
    }
  };

  const content = (
    <Form.Item name={name} {...formItemProps}>
      <Select
        showArrow
        allowClear
        filterOption={false}
        autoClearSearchValue={false}
        loading={isFetching}
        options={options}
        optionFilterProp="label"
        onPopupScroll={loadMore}
        onSearch={onSearch}
        onBlur={refresh}
        onFocus={onFocus}
        notFoundContent={isFetching ? Spinning : emptyComponent}
        className={`w-full ${className ?? ''}`}
        placeholder={placeholder ? i18next.t(placeholder) : null}
        {...rest}
      />
    </Form.Item>
  );

  if (isShowTooltip && isString(placeholder)) {
    return (
      <Tooltip placement="top" title={i18next.t(placeholder)}>
        {content}
      </Tooltip>
    );
  }

  return content;
}

export default InfiniteSelect;
